import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { service } from '@ember/service'
import type LogoutService from 'district-ui-client/services/logout'
import type SessionService from 'district-ui-client/services/session'
import { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import { LinkTo } from '@ember/routing'
import { eq } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import t from 'ember-intl/helpers/t'
import reLogo from '@images/reading-eggs.svg'
import rexLogo from '@images/reading-eggspress.svg'
import fpLogo from '@images/fast-phonics.svg'
import msLogo from '@images/mathseeds.svg'
import mxLogo from '@images/mathletics.svg'
import wlLogo from '@images/writing-legends.svg'
import type ActiveRouteService from 'district-ui-client/services/active-route'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { fn } from '@ember/helper'
import { runTask } from 'ember-lifeline'
import { array } from '@ember/helper'
import { ThemedDropdownContent } from 'district-ui-client/components/themed-dropdown'
import { ThemedMenu, ThemedMenuGroup, ThemedMenuItemLinkTo } from 'district-ui-client/components/themed-menu'
import { FeatureNotificationId } from 'district-ui-client/services/feature-notification'
import { FeatureNotification } from 'district-ui-client/helpers/feature-notification'
import { NotificationDot } from 'district-ui-client/components/notification-dot'
import { Lozenge } from 'district-ui-client/components/lozenge'
import type { ReportRegistryService } from 'district-ui-client/services/report-registry'
import { Product } from 'district-ui-client/domain/product'
import { SiteHeaderNavButton } from 'district-ui-client/components/site-header-nav-button'
import { IntegrationsSiteHeader } from 'district-ui-client/components/integrations/site-header'
import type { IntlService } from 'ember-intl'

interface Signature {
  Element: HTMLElement
}

const TAB_ORDER = [
  SubscriptionType.Reading,
  SubscriptionType.Mathletics,
  SubscriptionType.Maths,
  SubscriptionType.Writing,
]

export class SiteHeader extends Component<Signature> {
  @service logout!: LogoutService

  @service activeRoute!: ActiveRouteService

  @service session!: SessionService

  get subscriptionType() {
    return this.activeRoute.maybeSubscriptionType
  }

  get showDistrictName() {
    return !this.subscriptionType || this.activeRoute.isReporting
  }

  get tabSubscriptionTypes() {
    return this.session.currentDisco.subscriptionTypes
  }

  <template>
    <header
      class="flex items-center justify-between border-b-2 border-solid border-neutral-75 bg-white px-4 lg:px-8 print:hidden"
      ...attributes
    >
      <Tabs @districtSubscriptions={{this.tabSubscriptionTypes}} />
      {{#if this.showDistrictName}}
        <div class="hidden text-base font-medium text-neutral-400 lg:flex">{{this.session.currentDistrict.name}}</div>
      {{else}}
        <ProductLogos @subscriptionType={{this.subscriptionType}} class="hidden lg:flex" />
      {{/if}}
      <div class="flex space-x-4">
        <IntegrationsSiteHeader @subscriptionType={{this.subscriptionType}} />
        <Settings @isSettingsRoute={{this.activeRoute.isSettings}} />
        <VerticalDivide class="self-center bg-neutral-50" />
        <LogoutButton {{on "click" this.logout.logoutAndRedirect}} />
      </div>
    </header>
  </template>
}

interface SettingsSignature {
  Element: HTMLElement
  Args: {
    isSettingsRoute?: boolean
  }
}

class Settings extends Component<SettingsSignature> {
  // We need the LinkTo to handle the event first, while the element is still rendered, then close the dropdown.
  // Otherwise, the page hard refreshes. see: https://github.com/cibernox/ember-basic-dropdown/issues/528
  delayClose = (closeAction: () => unknown) => {
    runTask(this, () => {
      closeAction()
      // This is to prevent the button from staying focused after the dropdown closes
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    })
  }

  <template>
    <PopoverStandardDropdown as |dropdown status actions|>
      <SiteHeaderNavButton
        data-test-settings-trigger
        @currentWhen={{@isSettingsRoute}}
        @isOpen={{status.isOpen}}
        {{dropdown.makeTrigger}}
        ...attributes
      >
        <span class="flex items-center gap-2">
          <PhosphorIcon @icon="phosphor-gear-six-fill" />
          <div class="relative inline-block">
            {{#if (FeatureNotification FeatureNotificationId.SetAcademicStandards)}}
              <span class="mr-1">{{t "settings.title"}}</span>
              <NotificationDot class="absolute -right-1.5 -top-0.5" />
            {{else}}
              <span>{{t "settings.title"}}</span>
            {{/if}}
          </div>
          <PhosphorIcon @icon="phosphor-caret-down-bold" />
        </span>
      </SiteHeaderNavButton>
      <dropdown.content>
        {{! The button trigger box is bigger than the button itself.
        The negative margin here counters the my-3 + h-1 below the button, to end up with the intended 1 unit gap }}
        <ThemedDropdownContent class="!-mt-3" @constrainWidth={{true}}>
          <ThemedMenu>
            <ThemedMenuGroup>
              <ThemedMenuItemLinkTo
                class="flex items-center gap-2"
                @route="settings.district-coordinator"
                {{on "click" (fn this.delayClose actions.close)}}
              >
                <PhosphorIcon @icon="phosphor-user-circle-fill" />
                <span>
                  {{t "settings.myAccount"}}
                </span>
              </ThemedMenuItemLinkTo>
              <ThemedMenuItemLinkTo
                class="flex items-center gap-2"
                @route="settings.district-settings"
                {{on "click" (fn this.delayClose actions.close)}}
              >
                <PhosphorIcon @icon="phosphor-sliders-horizontal-bold" />
                <span class="flex-grow">
                  {{t "settings.district"}}
                </span>
                {{#if (FeatureNotification FeatureNotificationId.SetAcademicStandards)}}
                  <Lozenge class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
                {{/if}}
              </ThemedMenuItemLinkTo>
            </ThemedMenuGroup>
          </ThemedMenu>
        </ThemedDropdownContent>
      </dropdown.content>
    </PopoverStandardDropdown>
  </template>
}

class ProductLogos extends Component<{
  Element: HTMLDivElement
  Args: {
    subscriptionType?: SubscriptionType
  }
}> {
  @service reportRegistry!: ReportRegistryService

  get showFPLogo(): boolean {
    return this.reportRegistry.reportPathsForProduct(Product.FP).length > 0
  }

  <template>
    <div
      data-test-product-logos
      class="h-12 max-h-12 shrink-0 flex-nowrap items-center justify-center gap-3"
      ...attributes
    >
      {{#if (eq @subscriptionType SubscriptionType.Reading)}}
        <img src={{reLogo}} alt={{t "products.reading-eggs"}} />
        <img src={{rexLogo}} alt={{t "products.reading-eggspress"}} />
        {{#if this.showFPLogo}}
          <img src={{fpLogo}} alt={{t "products.fast-phonics"}} />
        {{/if}}
      {{/if}}
      {{#if (eq @subscriptionType SubscriptionType.Maths)}}
        <img class="h-10" src={{msLogo}} alt={{t "products.mathseeds"}} />
      {{/if}}
      {{#if (eq @subscriptionType SubscriptionType.Writing)}}
        <img src={{wlLogo}} alt={{t "products.writing-legends"}} />
      {{/if}}
      {{#if (eq @subscriptionType SubscriptionType.Mathletics)}}
        <img class="h-9" src={{mxLogo}} alt={{t "products.mathletics"}} />
      {{/if}}
    </div>
  </template>
}

const VerticalDivide: TOC<{
  Element: HTMLDivElement
}> = <template>
  <div class="inline-block h-6 w-0.5" ...attributes></div>
</template>

interface SiteHeaderNavLinkSignature {
  Element: HTMLAnchorElement
  Args: {
    route: string
    subscriptionType: SubscriptionType
  }
  Blocks: {
    default: []
  }
}

const SiteHeaderNavLink: TOC<SiteHeaderNavLinkSignature> = <template>
  <LinkTo
    @route={{@route}}
    @models={{array @subscriptionType}}
    class="group inline-block text-base font-medium transition-colors duration-75 ease-in-out focus:outline-none
      {{if (eq @subscriptionType SubscriptionType.Reading) 'selected:text-purply-pink-300'}}
      {{if (eq @subscriptionType SubscriptionType.Maths) 'selected:text-oceany-blue-350'}}
      {{if (eq @subscriptionType SubscriptionType.Mathletics) 'selected:text-oceany-blue-350'}}
      {{if (eq @subscriptionType SubscriptionType.Writing) 'selected:text-forest-green-350'}}
      selected:[&_.button-underline]:bg-current"
    ...attributes
  >
    {{! separator to keep text vertically centered, balancing the line on the other side }}
    <div class="h-1 w-full" />
    <div
      class="my-3 rounded-lg bg-white px-4 py-2 group-hover:bg-neutral-75 group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-blue-325 group-active:bg-neutral-100"
    >{{yield}}</div>
    <div class="button-underline h-1 w-full rounded-t-sm transition-colors duration-200 ease-in-out"></div>
  </LinkTo>
</template>

const LogoutButton: TOC<{
  Element: HTMLButtonElement
}> = <template>
  <button
    type="button"
    class="group inline-block text-base font-medium text-neutral-300 focus:outline-none"
    ...attributes
  >
    <div
      class="my-4 flex items-center gap-2 rounded-lg px-4 py-2 group-hover:bg-neutral-75 group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-blue-325 group-active:bg-neutral-100"
    >
      <PhosphorIcon @icon="phosphor-sign-out-bold" /><span>{{t "components.topNav.logout"}}</span>
    </div>
  </button>
</template>

class Tabs extends Component<{
  Element: HTMLDivElement
  Args: {
    districtSubscriptions: SubscriptionType[]
  }
}> {
  @service intl!: IntlService

  get tabs() {
    // sort the subscription types based on the defined order
    const subscriptionTypes = this.args.districtSubscriptions.sort((a, b) => {
      return TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b)
    })

    // build the labels from the subscription types
    // for the subscription type "maths", we use the label for "Mathseeds" rather than "Maths"
    // as we don't want to show "Mathletics" followed by "Maths" in the tabs
    return subscriptionTypes.map((subscriptionType) => {
      let label = ''
      if (subscriptionType === SubscriptionType.Maths) {
        label = this.intl.t('products.mathseeds')
      } else {
        label = this.intl.t(subscriptionType)
      }
      return {
        subscriptionType,
        label,
      }
    })
  }

  <template>
    <div class="flex items-center justify-center gap-3" ...attributes>
      {{#each this.tabs as |tab|}}
        <SiteHeaderNavLink
          data-test-subscription-tab={{tab.subscriptionType}}
          @route="subscription-type"
          @subscriptionType={{tab.subscriptionType}}
        >
          {{tab.label}}
        </SiteHeaderNavLink>
      {{/each}}
    </div>
  </template>
}
export default SiteHeader
